/**
 * Shared formatter utilities for cell specs
 *
 * These formatters handle special cases like disabled cells and conditional formatting.
 */

import { ValueFormatterParams } from "ag-grid-community";
import { formatters } from "@platform/design-system";

import { HeapedCommissionValue } from "../../../api/commissionHelpers";
import { getCellValueInCurrentColumn } from "../../../components/PlanEditor/utilities/agGridUtils";
import { formatEnumValue } from "../../../components/PlanEditor/utilities/enumFormatters";

import { EditableWhen } from "./types";

/**
 * Checks if a cell is editable based on its editable function
 *
 * Helper function to determine if a cell should display its value or show "-"
 * for disabled cells. Used by disabled-aware formatters that check context-based
 * conditional editability (e.g., createContextConditionalEditable).
 *
 * @param params - AG Grid value formatter parameters
 * @returns True if the cell is editable, false if disabled
 */
const isCellEditable = (params: ValueFormatterParams): boolean => {
  if (!params?.colDef) return true;

  const isEditable =
    typeof params.colDef.editable === "function"
      ? params.node !== null &&
        params.colDef.editable({ ...params, node: params.node })
      : params.colDef.editable !== false;

  return isEditable;
};

/**
 * Checks if a value is empty (undefined, null, or empty string)
 *
 * @param value - Value to check
 * @returns True if the value is empty
 */
const isValueEmpty = (value: unknown): boolean =>
  value === undefined || value === null || value === "";

/**
 * Creates a currency formatter that respects editableWhen disabled state.
 *
 * When a cell has editableWhen configuration and the dependent cell's value
 * is in the disabledValues list, this formatter returns "-" instead of
 * formatting the value (which would show $0 for undefined/0 values).
 *
 * @param options - Intl.NumberFormatOptions for currency formatting
 * @returns A formatter function that returns "-" for disabled cells or formatted currency
 *
 * @example
 * dollarAmount: {
 *   cellRenderer: createDisabledAwareCurrencyFormatter({
 *     currency: "USD",
 *     minimumFractionDigits: 0,
 *     maximumFractionDigits: 0,
 *   }),
 * }
 */
export const createDisabledAwareCurrencyFormatter = (
  options: Intl.NumberFormatOptions = {},
) => {
  const currencyFormatter = formatters.currency(options);

  return (params: ValueFormatterParams): string => {
    // Check if this cell has editableWhen configuration
    const editableWhen = params.data?.benefit?.editableWhen as
      | EditableWhen
      | undefined;

    if (editableWhen) {
      // Get the value from the dependent cell in the same column
      const dependentValue = getCellValueInCurrentColumn(
        params,
        editableWhen.dependsOn,
      );

      // Check if the dependent value disables this cell
      // Treat empty string and undefined/null as disabling values (field not set)
      // Also check if value is in disabledValues array
      const isDisabled =
        dependentValue === undefined ||
        dependentValue === null ||
        dependentValue === "" ||
        editableWhen.disabledValues?.includes(dependentValue as string);

      // Return "-" for disabled cells
      if (isDisabled) {
        return "-";
      }
    }

    // For enabled cells, check if value is undefined/null/"" and show empty string
    if (
      params.value === undefined ||
      params.value === null ||
      params.value === ""
    ) {
      return "";
    }

    // For enabled cells with valid values, use the standard currency formatter
    return currencyFormatter(params);
  };
};

/**
 * Creates a number formatter that respects editableWhen disabled state.
 *
 * Similar to createDisabledAwareCurrencyFormatter but formats plain numbers
 * without currency symbols. Used for duration fields (days, months) or other
 * numeric values that shouldn't display as currency.
 *
 * When a cell has editableWhen configuration and the dependent cell's value
 * is in the disabledValues list, this formatter returns "-" instead of
 * formatting the value.
 *
 * @param options - Intl.NumberFormatOptions for number formatting
 * @returns A formatter function that returns "-" for disabled cells or formatted number
 *
 * @example
 * numberField: {
 *   cellRenderer: createDisabledAwareNumberFormatter({
 *     minimumFractionDigits: 0,
 *     maximumFractionDigits: 0,
 *   }),
 * }
 */
export const createDisabledAwareNumberFormatter =
  (options: Intl.NumberFormatOptions = {}) =>
  (params: ValueFormatterParams): string => {
    // Check if this cell has editableWhen configuration
    const editableWhen = params.data?.benefit?.editableWhen as
      | EditableWhen
      | undefined;

    if (editableWhen) {
      // Get the value from the dependent cell in the same column
      const dependentValue = getCellValueInCurrentColumn(
        params,
        editableWhen.dependsOn,
      );

      // Check if the dependent value disables this cell
      // Treat empty string and undefined/null as disabling values (field not set)
      // Also check if value is in disabledValues array
      const isDisabled =
        dependentValue === undefined ||
        dependentValue === null ||
        dependentValue === "" ||
        editableWhen.disabledValues?.includes(dependentValue as string);

      // Return "-" for disabled cells
      if (isDisabled) {
        return "-";
      }
    }

    // For enabled cells, check if value is undefined/null/"" and show empty string
    if (
      params.value === undefined ||
      params.value === null ||
      params.value === ""
    ) {
      return "";
    }

    // For enabled cells with valid values, format as plain number
    const numberValue = Number(params.value);
    if (isNaN(numberValue)) return "";

    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
      maximumFractionDigits: options.maximumFractionDigits ?? 0,
      ...options,
    }).format(numberValue);
  };

/**
 * Creates a percentage formatter that respects editableWhen disabled state.
 *
 * Similar to createDisabledAwareNumberFormatter but formats numbers as percentages
 * with a % symbol. Used for percentage fields that should be conditionally disabled.
 *
 * When a cell has editableWhen configuration and the dependent cell's value
 * is in the disabledValues list, this formatter returns "-" instead of
 * formatting the value.
 *
 * @param options - Intl.NumberFormatOptions for percentage formatting (optional)
 * @returns A formatter function that returns "-" for disabled cells or formatted percentage
 *
 * @example
 * percentageAmount: {
 *   cellRenderer: createDisabledAwarePercentageFormatter(),
 * }
 */
export const createDisabledAwarePercentageFormatter =
  (options: Intl.NumberFormatOptions = {}) =>
  (params: ValueFormatterParams): string => {
    // Check if this cell has editableWhen configuration
    const editableWhen = params.data?.benefit?.editableWhen as
      | EditableWhen
      | undefined;

    if (editableWhen) {
      // Get the value from the dependent cell in the same column
      const dependentValue = getCellValueInCurrentColumn(
        params,
        editableWhen.dependsOn,
      );

      // Check if the dependent value disables this cell
      // Treat empty string and undefined/null as disabling values (field not set)
      // Also check if value is in disabledValues array
      const isDisabled =
        dependentValue === undefined ||
        dependentValue === null ||
        dependentValue === "" ||
        editableWhen.disabledValues?.includes(dependentValue as string);

      // Return "-" for disabled cells
      if (isDisabled) {
        return "-";
      }
    }

    // For enabled cells, check if value is undefined/null/"" and show empty string
    if (
      params.value === undefined ||
      params.value === null ||
      params.value === ""
    ) {
      return "";
    }

    // For enabled cells with valid values, format as percentage
    const numberValue = Number(params.value);
    if (isNaN(numberValue)) return "";

    return `${new Intl.NumberFormat("en-US", {
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
      maximumFractionDigits: options.maximumFractionDigits ?? 2,
      ...options,
    }).format(numberValue)}%`;
  };

/**
 * Creates a "times salary" formatter that respects editableWhen disabled state.
 *
 * @deprecated Use createDisabledAwareNumberFormatter with suffix: "times salary" instead
 * This formatter is kept for backward compatibility but should not be used in new code.
 *
 * @example
 * // Old way (deprecated)
 * cellRenderer: createTimesSalaryFormatter({ ... })
 *
 * // New way (preferred)
 * cellRenderer: createDisabledAwareNumberFormatter({ ... }),
 * suffix: "times salary"
 *
 * @returns A formatter function that returns "-" for disabled cells or "X times salary"
 */
export const createTimesSalaryFormatter =
  (options: Intl.NumberFormatOptions = {}) =>
  (params: ValueFormatterParams): string => {
    // Check if this cell has editableWhen configuration
    const editableWhen = params.data?.benefit?.editableWhen as
      | EditableWhen
      | undefined;

    if (editableWhen) {
      // Get the value from the dependent cell in the same column
      const dependentValue = getCellValueInCurrentColumn(
        params,
        editableWhen.dependsOn,
      );

      // Check if the dependent value disables this cell
      const isDisabled =
        dependentValue === undefined ||
        dependentValue === null ||
        dependentValue === "" ||
        editableWhen.disabledValues?.includes(dependentValue as string);

      // Return "-" for disabled cells
      if (isDisabled) {
        return "-";
      }
    }

    // For enabled cells, check if value is undefined/null and show "-"
    if (params.value === undefined || params.value === null) {
      return "-";
    }

    const numberValue = Number(params.value);
    if (isNaN(numberValue)) return "-";

    const formattedNumber = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
      maximumFractionDigits: options.maximumFractionDigits ?? 0,
      ...options,
    }).format(numberValue);

    return `${formattedNumber} times salary`;
  };

/**
 * Creates a text formatter that respects context-based conditional editability.
 *
 * When a cell uses createContextConditionalEditable and is disabled based on
 * context values (e.g., policyPeriod), this formatter returns "-" instead of
 * displaying the text value.
 *
 * This is different from editableWhen formatters - it checks the editable function
 * result directly rather than looking at editableWhen configuration.
 *
 * @returns A formatter function that returns "-" for disabled cells or the text value
 *
 * @example
 * currentCarrierName: {
 *   editable: createContextConditionalEditable("policyPeriod", ["newBusiness", "alternate"]),
 *   valueFormatter: createDisabledAwareTextFormatter(),
 * }
 */
export const createDisabledAwareTextFormatter =
  () =>
  (params: ValueFormatterParams): string => {
    // If the cell is not editable (disabled), return "-"
    if (!isCellEditable(params)) {
      return "-";
    }

    // For enabled cells, return the value or "-" if empty
    if (isValueEmpty(params.value)) {
      return "-";
    }

    return String(params.value);
  };

/**
 * Creates an enum formatter that respects context-based conditional editability.
 *
 * Similar to createDisabledAwareTextFormatter but formats enum values with proper
 * label transformations (camelCase to Title Case, custom labels, acronyms) when enabled.
 * When a cell uses createContextConditionalEditable and is disabled based on
 * context values (e.g., policyPeriod), this formatter returns "-" instead of
 * displaying the enum value.
 *
 * This is different from editableWhen formatters - it checks the editable function
 * result directly rather than looking at editableWhen configuration.
 *
 * Supports both AG Grid contexts via function overloads:
 * - valueFormatter (params: ValueFormatterParams): Checks editability and returns "-" for disabled
 * - cellEditorParams.formatValue (value: string): Just formats the enum value for dropdown display
 *
 * @param customLabels - Optional full override map for specific enum values
 * @returns A formatter function that returns "-" for disabled cells or formatted enum value
 *
 * @example
 * currentCommissionType: {
 *   editable: createContextConditionalEditable("policyPeriod", ["newBusiness", "alternate"]),
 *   cellEditorParams: { formatValue: createDisabledAwareEnumFormatter({ standard: "Carrier Standard" }) },
 *   valueFormatter: createDisabledAwareEnumFormatter({ standard: "Carrier Standard" }),
 * }
 */
export function createDisabledAwareEnumFormatter(
  customLabels?: Record<string, string>,
): (params: ValueFormatterParams | string) => string {
  return (paramsOrValue: ValueFormatterParams | string): string => {
    // If called with just a string (from cellEditorParams.formatValue)
    // Just format the enum value for the dropdown - no disabled state check needed
    if (typeof paramsOrValue === "string") {
      return formatEnumValue(paramsOrValue, customLabels);
    }

    // Guard against null params (can occur during AG Grid lifecycle events)
    if (!paramsOrValue) return "";

    // Called with ValueFormatterParams (from valueFormatter)
    // Check editability and format accordingly
    const params = paramsOrValue;

    // If the cell is not editable (disabled), return "-"
    if (!isCellEditable(params)) {
      return "-";
    }

    // For enabled cells, return "-" if value is empty
    if (isValueEmpty(params.value)) {
      return "-";
    }

    // For enabled cells with valid values, format as enum
    return formatEnumValue(String(params.value), customLabels);
  };
}

/**
 * Formats heaped commission values for display and export.
 * Converts {first, thereafter} object to readable string format. Renders "-"
 * when the value is missing, a malformed JSON string, or not a well-formed
 * {first, thereafter} pair.
 */
export const formatHeapedCommissionValue = (
  params: ValueFormatterParams,
): string => {
  let value = params.value as HeapedCommissionValue | null | undefined;
  if (!value || (typeof value !== "object" && typeof value !== "string")) {
    return "-";
  }

  if (typeof value === "string") {
    try {
      value = JSON.parse(value) as HeapedCommissionValue;
    } catch {
      return "-";
    }
  }

  if (!value || typeof value !== "object") {
    return "-";
  }

  const { first, thereafter } = value;

  if (typeof first !== "number" || typeof thereafter !== "number") {
    return "-";
  }

  return `First: ${first}%, Thereafter: ${thereafter}%`;
};

/**
 * Formats a graded commission value (a single "highest %" number) for display
 * and export. Renders "-" when null/undefined, since graded commissions are
 * allowed to have no defined percentage.
 */
export const formatGradedCommissionValue = (
  params: ValueFormatterParams,
): string => {
  let value = params.value as number | string | null | undefined;
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "string") {
    value = parseFloat(value);
  }

  if (typeof value !== "number" || isNaN(value)) {
    return "-";
  }

  return `${value}%`;
};
